import {
    TFillFormFunction,
    TFunc,
    TOnDropdownSelectCb,
    TOnPaginationClickCb,
    TOnTableChangeCb,
    TUpdateFunction,
} from "./types";
import {
    IDropdownOnChangeResult,
    IFixedForm,
    IPaginationOptions,
    ISimpleForm,
    ITableBody,
    ITableFooter,
    ITableHead,
} from "./interfaces";
import {
    IAccountsTableContent, ICardsTSumAccountData, IDocsData,
    IP2PCardData,
    IP2PCommissionInfo,
    IP2PFormAccountData,
    IP2PResponse,
    IRowResponseData,
    ITableAction,
    ITableRequestData,
    ITableResponseData,
} from "../src/protocols/interfaces";
import {
    IColumnSettings,
    IColumnValueSettings,
    ICurrencyDropdownItem,
    IDropdownItem,
    IDropdownOptions,
    IHTMLFormElement,
    IHTMLFormSettings,
    IHTMLTableOptions,
    IHTMLTableSettings,
} from "../src/tables/interfaces";
import {IAskNewDoc} from "../src/banks/interfaces";

/* eslint-disable */
declare global {
    interface Window {
        globals: {
            lastUpdateFunction?: TUpdateFunction,
            lastDefaultSettings?: ITableRequestData,
        } & Record<string,any>;
    }
}
/* eslint-enable */

window.globals = window.globals || {};

function reSelectDropdownItem(menu: HTMLUListElement, selectedItem: IDropdownItem): void {
    [...menu.children].forEach(child => {
        const button: HTMLButtonElement = child?.querySelector("button");
        if (!button) {
            return;
        }
        const value: string = button.dataset.value;
        button.classList.toggle("selected", selectedItem.value === value);
    });
}

export function makeDropdown(options: IDropdownOptions, onChange?: TOnDropdownSelectCb): HTMLElement {
    let selectedItem: IDropdownItem = options.items.find(item => item.value === options.selectedItemValue);

    const dropdown: HTMLDivElement = document.createElement("div");
    dropdown.className = ("dropdown " + (options.className || "")).trim();
    dropdown.setAttribute("name", options.name);

    const mainButton: HTMLButtonElement = document.createElement("button");
    mainButton.className = "btn btn-light dropdown-toggle";
    mainButton.type = "button";
    mainButton.setAttribute("data-coreui-toggle", "dropdown");
    mainButton.ariaExpanded = "false";
    mainButton.innerHTML = selectedItem?.text ?? options.placeholderText ?? "";

    const menu: HTMLUListElement = document.createElement("ul");
    menu.className = "dropdown-menu";

    options.items.forEach(item => {
        const button: HTMLButtonElement = document.createElement("button");
        button.className = `dropdown-item${item === selectedItem ? " selected" : ""}`;
        button.setAttribute("data-value", item.value);
        button.innerHTML = item.text;
        button.type = "button";
        button.addEventListener("click", () => {
            mainButton.innerHTML = item.text;
            selectedItem = item;
            reSelectDropdownItem(menu, selectedItem);
            onChange && onChange({
                name: options.name,
                value: item.value,
                text: item.text,
            });
        });

        const li: HTMLLIElement = document.createElement("li");
        li.append(button);
        menu.append(li);
    });

    dropdown.append(mainButton, menu);

    return dropdown;
}

function makePaginationLi(
    nextPage: number,
    text: string,
    disabled: boolean,
    onClick?: TOnPaginationClickCb,
): HTMLLIElement {
    const li: HTMLLIElement = document.createElement("li");
    li.className = "page-item";
    const button: HTMLButtonElement = document.createElement("button");
    button.className = `page-link${disabled ? " disabled" : ""}`;
    button.innerHTML = `<span>${text}</span>`;
    li.append(button);
    button.addEventListener("click", () => {
        onClick && onClick(nextPage);
    });
    return li;
}

function makePrevLi(options: IPaginationOptions, onClick?: TOnPaginationClickCb): HTMLLIElement {
    return makePaginationLi(
        options.currentPage - 1,
        "&lt;",
        options.currentPage < 2 || options.lastPage < 2,
        onClick,
    );
}

function makeNextLi(options: IPaginationOptions, onClick?: TOnPaginationClickCb): HTMLLIElement {
    return makePaginationLi(
        options.currentPage + 1,
        "&gt;",
        options.currentPage >= options.lastPage || options.lastPage < 2,
        onClick,
    );
}

function makePageLi(page: number, options: IPaginationOptions, onClick?: TOnPaginationClickCb): HTMLLIElement {
    return makePaginationLi(
        page,
        page && page.toString() || "...",
        !page || page === options.currentPage,
        onClick,
    );
}

export function makePagination(options: IPaginationOptions, onClick?: TOnPaginationClickCb): HTMLElement {
    const nav: HTMLElement = document.createElement("nav");
    const ul: HTMLUListElement = document.createElement("ul");
    ul.className = "pagination mb-0";
    ul.append(makePrevLi(options, onClick));
    ul.append(makePageLi(1, options, onClick));
    if (options.currentPage - 2 > 3) {
        ul.append(makePageLi(0, options, onClick));
    }

    let start: number = options.currentPage - 2;
    if (start < 4) {
        start = 2;
    }
    let end: number = options.currentPage + 2;
    if (end > options.lastPage - 3) {
        end = options.lastPage - 1;
    }

    for (let page: number = start; page <= end; ++page) {
        ul.append(makePageLi(page, options, onClick));
    }

    if (options.currentPage + 2 < options.lastPage - 2) {
        ul.append(makePageLi(0, options, onClick));
    }
    if (options.lastPage > 1) {
        ul.append(makePageLi(options.lastPage, options, onClick));
    }
    ul.append(makeNextLi(options, onClick));
    nav.append(ul);
    return nav;
}

function makeSearchBox(
    _options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    update: TUpdateFunction,
): HTMLElement {
    const randomID: string = "searchID" + (Date.now() * Math.random()).toString();
    const searchBox: HTMLDivElement = document.createElement("div");
    searchBox.className = "input-group me-2";
    const label: HTMLLabelElement = document.createElement("label");
    label.className = "input-group-text";
    label.setAttribute("for", randomID);
    label.innerHTML = `<svg class="icon">
                    <use xlink:href="/coreui/vendors/@coreui/icons/svg/linear.svg#cil-search"></use>
                </svg>`;

    const input: HTMLInputElement = document.createElement("input");
    input.className = "form-control";
    input.type = "text";
    input.name = "search";
    input.id = randomID;
    input.value = defaultSettings?.search ?? "";

    let searchTimeout: NodeJS.Timeout;
    input.addEventListener("keyup", () => {
        defaultSettings.search = input.value;
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        searchTimeout = setTimeout(() => {
            update(defaultSettings);
        }, 300);
    });

    searchBox.append(label, input);
    return searchBox;
}

function makeButton(
    className: string,
    icon: string,
    activated: boolean = false,
): HTMLButtonElement {
    const button: HTMLButtonElement = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.classList.toggle("activated", activated);
    button.innerHTML = `<svg class="icon">
                <use xlink:href="/coreui/vendors/@coreui/icons/svg/linear.svg#${icon}"></use>
            </svg>`;
    return button;
}

function makeActionButton(action: ITableAction, mini: boolean): HTMLButtonElement {
    const button: HTMLButtonElement = document.createElement("button");
    button.type = "button";
    button.className = `btn btn-light ${mini ? "me-2" : ""}`;
    if (action.color) {
        button.style.color = action.color;
    }
    if (action.icon) {
        button.innerHTML = `<svg class="icon${!mini ? " me-1" : ""}">
                <use xlink:href="/coreui/vendors/@coreui/icons/svg/linear.svg#${action.icon}"></use>
            </svg>`;
    }
    if (action.text) {
        if (mini) {
            button.title = action.text;
        } else {
            const span: HTMLSpanElement = document.createElement("span");
            span.innerHTML = action.text;
            button.append(span);
        }
    }
    return button;
}

export async function sendRequest<I>(url: string, data: any): Promise<I> {
    return fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
            "content-type": "application/json; charset=utf-8",
        },
    }).then(r => r.json());
}

export function makeFixedForm(titleText?: string, button?: HTMLButtonElement): IFixedForm {
    const parent: HTMLDivElement = document.createElement("div");
    parent.className = "fixed-form d-none";
    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        parent.classList.remove("show");
    });

    button?.addEventListener("click", () => {
        parent.classList.add("show");
    });

    const content: HTMLDivElement = document.createElement("div");
    content.className = "content card p-2";

    const body: HTMLDivElement = document.createElement("div");
    body.className = "card-body";

    if (titleText) {
        const title: HTMLHeadingElement = document.createElement("h5");
        title.className = "card-title";
        title.innerHTML = titleText;
        body.append(title);
    }
    content.append(body);
    parent.append(content);

    return {parent, body};
}

export function makeFixedFormModal(titleText: string, button?: HTMLButtonElement): IFixedForm {
    const randomID: string = "searchID" + (Date.now() * Math.random()).toString();

    const parent: HTMLDivElement = document.createElement("div");
    parent.className = "modal fade";
    parent.id = randomID;

    button.setAttribute("data-coreui-toggle", "modal");
    button.setAttribute("data-coreui-target", `#${parent.id}`);

    const dialog: HTMLDivElement = document.createElement("div");
    dialog.className = "modal-dialog modal-dialog-centered modal-sm";
    const content: HTMLDivElement = document.createElement("div");
    content.className = "modal-content";
    dialog.append(content);

    const title: HTMLDivElement = document.createElement("div");
    title.className = "modal-header";
    title.innerHTML = `<h5 class="modal-title">${titleText}</h5>
        <button type="button" class="btn-close" data-coreui-dismiss="modal" aria-label="Close"></button>`;
    content.append(title);

    const body: HTMLDivElement = document.createElement("div");
    body.className = "modal-body";
    content.append(body);

    parent.append(dialog);
    return {parent, body};
}

function makeFilterForm(
    filterButton: HTMLButtonElement,
    options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    update: TUpdateFunction,
): HTMLElement {
    const {parent, body}: IFixedForm = makeFixedForm("Filter", filterButton);

    options.filter.forEach(value => {
        const form: HTMLDivElement = document.createElement("div");
        form.className = "form-group mb-3";

        const label: HTMLDivElement = document.createElement("div");
        label.className = "label mb-1";
        label.innerHTML = value.label;

        const dropdownOptions: IDropdownOptions = {
            name: value.name,
            items: [{value: "", text: "All"}, ...value.items],
            selectedItemValue: defaultSettings[value.name] ?? "",
        };

        form.append(label, makeDropdown(dropdownOptions, (result: IDropdownOnChangeResult) => {
            if (!result.value) {
                delete defaultSettings.filter[result.name];
            } else {
                defaultSettings.filter[result.name] = result.value;
            }
            filterButton.classList.toggle("activated", !!Object.keys(defaultSettings.filter).length);
            update(defaultSettings);
        }));
        body.append(form);
    });

    return parent;
}

function makeSortingForm(
    button: HTMLButtonElement,
    options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    update: TUpdateFunction,
): HTMLElement {
    const {parent, body}: IFixedForm = makeFixedForm("Sort by", button);

    const form: HTMLDivElement = document.createElement("div");
    form.className = "form-group mb-3";

    const dropdownOptions: IDropdownOptions = {
        name: "sort",
        items: [{value: "", text: "Default"}, ...options.sorting.reduce((acc: IDropdownItem[], value) => {
            acc.push(
                {value: `${value.value}_asc`, text: `${value.text} &uarr;`},
                {value: `${value.value}_desc`, text: `${value.text} &darr;`},
            );
            return acc;
        }, [])],
        selectedItemValue: defaultSettings.sort,
    };

    form.append(makeDropdown(dropdownOptions, (result: IDropdownOnChangeResult) => {
        defaultSettings.sort = result.value;
        button.classList.toggle("activated", !!defaultSettings.sort);
        update(defaultSettings);
    }));

    body.append(form);
    return parent;
}

function createSimpleFormElement(element: IHTMLFormElement, value: string = undefined): HTMLElement {
    const formGroup: HTMLDivElement = document.createElement("div");
    formGroup.className = "form-group formGroup";
    if (element.label) {
        const label: HTMLDivElement = document.createElement("div");
        label.className = "label mb-1";
        label.innerHTML = element.label;
        formGroup.append(label);
    }
    if (element.type === "dropdown") {
        const input: HTMLInputElement = document.createElement("input");
        input.type = "hidden";
        input.name = element.name;
        input.value = value ?? element.selectedItemValue ?? "";
        const dropdown: HTMLElement = makeDropdown({
            name: element.name,
            selectedItemValue: value ?? element.selectedItemValue,
            items: element.items ?? [],
            className: element.className ?? undefined,
        }, (result: IDropdownOnChangeResult) => {
            input.value = result.value;
            element.onChange && element.onChange(result);
        });
        dropdown.append(input);
        formGroup.append(dropdown);
    } else if (element.type === "error") {
        formGroup.id = element.id ?? `errorID_${(Date.now() * Math.random()).toString(36)}`;
    } else if (element.type === "button") {
        formGroup.classList.add("text-end");
        const hidden: HTMLInputElement = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = element.name;
        hidden.value = "0";
        const button: HTMLButtonElement = document.createElement("button");
        button.innerHTML = element.selectedItemValue ?? element.value ?? "";
        button.className = element.className ?? "";
        if (button.classList.contains("loadingButton") && !button.innerHTML.includes("spinner-border")) {
            button.prepend(createSpinner());
        }
        button.addEventListener("click", () => {
            hidden.value = Date.now().toString();
        });
        formGroup.append(hidden, button);
    } else if (element.type === "label") {
        // do nothing
    } else if (element.type === "checkbox") {
        const input: HTMLInputElement = document.createElement("input");
        input.id = `checkbox_${(Date.now() * Math.random()).toString(36)}`;
        input.type = element.type;
        input.name = element.name;
        input.value = "1";
        input.checked = !!value;
        input.className = `form-check-input ${element.className ?? ""}`;
        input.disabled = element.disabled ?? false;
        const inputGroup: HTMLDivElement = document.createElement("div");
        inputGroup.className = "form-check";
        inputGroup.append(input);
        if (element.afterInput) {
            const label: HTMLLabelElement = document.createElement("label");
            label.className = "form-check-label";
            label.innerHTML = element.afterInput;
            label.setAttribute("for", input.id);
            inputGroup.append(label);
        }
        formGroup.append(inputGroup);
    } else {
        const input: HTMLInputElement = document.createElement("input");
        input.type = element.type;
        input.name = element.name;
        input.value = value ?? element.selectedItemValue ?? element.value ?? "";
        input.placeholder = element.placeholderText ?? "";
        input.className = `form-control ${element.className ?? ""}`;
        input.disabled = element.disabled ?? false;
        const inputGroup: HTMLDivElement = document.createElement("div");
        inputGroup.className = "input-group";
        if (element.beforeInput) {
            const span: HTMLSpanElement = document.createElement("span");
            span.className = "input-group-text beforeInput";
            span.innerHTML = element.beforeInput;
            inputGroup.append(span);
        }
        inputGroup.append(input);
        if (element.afterInput) {
            const span: HTMLSpanElement = document.createElement("span");
            span.className = "input-group-text afterInput";
            span.innerHTML = element.afterInput;
            inputGroup.append(span);
        }
        formGroup.append(inputGroup);
    }
    return formGroup;
}

function makeSimpleForm(settings: IHTMLFormSettings): ISimpleForm {
    const form: HTMLFormElement = document.createElement("form");
    handleTableFormSubmit(form);
    form.action = settings.action;

    const resetForm: TFunc = () => {
        form.innerHTML = "";
        settings.elements.forEach(element => {
            form.append(createSimpleFormElement(element));
        });
    };
    resetForm();
    const fillForm: TFillFormFunction = (item: Record<string, any>) => {
        form.innerHTML = "";
        settings.elements.forEach(element => {
            form.append(createSimpleFormElement(element, item[element.name]));
        });
    };
    return {form, resetForm, fillForm};
}

function searchErrorID(form: HTMLFormElement): string {
    const el: Element = [...form.children].find(el => el.id.indexOf("errorID_") === 0);
    if (!el) {
        return "";
    }
    return el.id;
}

function makeCreatingForm(
    button: HTMLButtonElement,
    options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    update: TUpdateFunction,
): HTMLElement {
    const {parent, body}: IFixedForm = makeFixedForm("Create new", button);
    const {form, resetForm}: ISimpleForm = makeSimpleForm(options.creating);
    form.onResponse = (text: string) => {
        const errorID: string = searchErrorID(form);
        try {
            const data: ITableResponseData<any> = JSON.parse(text);
            if (data.status === "error") {
                errorID && showFormError(errorID, "danger", data.msg);
                return;
            }
            parent.classList.remove("show");
            resetForm();
            update(defaultSettings);
        } catch (err) {
            errorID && showFormError(errorID, "danger", err.toString());
        }
    };
    body.append(form);
    return parent;
}

function makeTableHead(
    options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    update: TUpdateFunction,
): ITableHead {
    const parent: HTMLDivElement = document.createElement("div");
    parent.className = "card flex-row p-2 mb-2 tableHead";
    if (options.isSearchBox) {
        parent.append(makeSearchBox(options, defaultSettings, update));
    }
    if (options.filter?.length) {
        const filterButton: HTMLButtonElement = makeButton(
            "btn btn-light me-2",
            "cil-filter",
            !!Object.keys(defaultSettings.filter).length,
        );
        filterButton.title = "Filter";
        const filterForm: HTMLElement = makeFilterForm(filterButton, options, defaultSettings, update);
        parent.append(filterButton, filterForm);
    }
    if (options.sorting?.length) {
        const sortingButton: HTMLButtonElement = makeButton(
            "btn btn-light me-2",
            "cil-sort-alpha-down",
            !!defaultSettings.sort,
        );
        sortingButton.title = "Sorting";
        const sortingForm: HTMLElement = makeSortingForm(sortingButton, options, defaultSettings, update);
        parent.append(sortingButton, sortingForm);
    }
    const updateButton: HTMLButtonElement = makeButton(
        "loadingButton btn btn-light me-2",
        "cil-reload",
    );
    updateButton.title = "Update list";
    updateButton.innerHTML += `<div class="spinner-border spinner-border-sm text-info" role="status">
        <span class="visually-hidden">Loading...</span>
    </div>`;
    updateButton.addEventListener("click", () => {
        update(defaultSettings);
    });
    parent.append(updateButton);
    if (options.updateDataURL) {
        const updateDataButton: HTMLButtonElement = makeButton(
            "loadingButton btn btn-light me-2",
            "cil-sync",
        );
        updateDataButton.title = "Update transactions";
        updateDataButton.innerHTML += `<div class="spinner-border spinner-border-sm text-info" role="status">
    <span class="visually-hidden">Loading...</span>
</div>`;
        updateDataButton.addEventListener("click", async () => {
            if (!confirm(`Are you sure you want to ${updateDataButton.title}?`)) {
                return;
            }
            updateDataButton.classList.add("loading");
            try {
                await sendRequest(options.updateDataURL, {});
            } catch (err) {
                console.error(err);
                throw err;
            } finally {
                updateDataButton.classList.remove("loading");
            }
            update(defaultSettings);
        });
        parent.append(updateDataButton);
    }
    if (options.showUnknownOutgoingsURL) {
        const showUnknownButton: HTMLButtonElement = makeButton(
            "loadingButton btn btn-light me-2",
            "cil-verical-split",
        );
        showUnknownButton.title = "Unknown outgoing transactions...";
        showUnknownButton.addEventListener("click", () => {
            location.href = options.showUnknownOutgoingsURL;
        });
        parent.append(showUnknownButton);
    }
    if (options.showDailyStatisticsURL) {
        const showDailyStatisticsButton: HTMLButtonElement = makeButton(
            "loadingButton btn btn-light me-2",
            "cil-bar-chart",
        );
        showDailyStatisticsButton.title = "Daily statistics...";
        showDailyStatisticsButton.addEventListener("click", () => {
            location.href = options.showDailyStatisticsURL;
        });
        parent.append(showDailyStatisticsButton);
    }
    if (options.creating) {
        const creatingButton: HTMLButtonElement = makeButton(
            "loadingButton btn btn-success rounded-pill createButton",
            "cil-plus",
        );
        creatingButton.title = "Create new";
        const creatingForm: HTMLElement = makeCreatingForm(creatingButton, options, defaultSettings, update);
        parent.append(creatingButton, creatingForm);
    }
    return {
        head: parent,
        updateButton,
    };
}

export function makeMainTable(
    options: IHTMLTableOptions,
    _defaultSettings: IHTMLTableSettings,
    _update: TUpdateFunction,
): ITableBody {
    const parent: HTMLDivElement = document.createElement("div");
    parent.className = "card p-2 mb-2";
    const table: HTMLTableElement = document.createElement("table");
    table.className = "table table-striped dataTable";
    const thead: HTMLTableSectionElement = document.createElement("thead");
    const theadTR: HTMLTableRowElement = document.createElement("tr");
    for (const name in options.columns) {
        const th: HTMLTableCellElement = document.createElement("th");
        th.scope = "col";
        if (name === "actions") {
            th.width = "100px";
        }
        th.setAttribute("data-name", name);
        th.innerHTML = options.columns[name].title;
        theadTR.append(th);
    }
    thead.append(theadTR);
    const tbody: HTMLTableSectionElement = document.createElement("tbody");
    tbody.innerHTML = `<tr>
            <td class="text-center" colspan="${Object.keys(options.columns).length}">Loading...</td>
        </tr>`;
    table.append(thead, tbody);
    parent.append(table);
    return {parent, tbody};
}

export function makeTableFooter(
    _options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    update: TUpdateFunction,
): ITableFooter {
    const parent: HTMLDivElement = document.createElement("div");
    parent.className = "card p-2 mb-2";
    const sum: HTMLDivElement = document.createElement("div");
    sum.className = "d-flex flex-row";
    const footer: HTMLDivElement = document.createElement("div");
    footer.className = "d-flex flex-row";
    const paginationParent: HTMLDivElement = document.createElement("div");
    paginationParent.className = "me-2";
    paginationParent.append(makePagination({
        currentPage: 1,
        lastPage: 1,
    }));
    const dropdown: HTMLElement = makeDropdown({
        name: "countPerPage",
        selectedItemValue: defaultSettings.countPerPage.toString(),
        items: [
            {value: "10", text: "10 / page"},
            {value: "25", text: "25 / page"},
            {value: "50", text: "50 / page"},
            {value: "100", text: "100 / page"},
            {value: "200", text: "200 / page"},
            {value: "300", text: "300 / page"},
        ],
    }, (result: IDropdownOnChangeResult) => {
        defaultSettings.countPerPage = parseInt(result.value);
        update(defaultSettings);
    });
    const pages: HTMLDivElement = document.createElement("div");
    pages.className = "d-flex mw-auto me-2";

    footer.append(paginationParent, dropdown, pages);
    parent.append(sum, footer);
    return {parent, sum, paginationParent, pages};
}

function setPagination(
    defaultSettings: IHTMLTableSettings,
    footer: ITableFooter,
    response: ITableResponseData<any>,
    update: TUpdateFunction,
) {
    footer.paginationParent.innerHTML = "";
    footer.paginationParent.append(makePagination({
        currentPage: response.page,
        lastPage: response.lastPage,
    }, (result: number) => {
        defaultSettings.page = result;
        update(defaultSettings);
    }));
    if (response.page) {
        defaultSettings.page = response.page;
    }
}

function setPages(
    footer: ITableFooter,
    response: ITableResponseData<any>,
) {
    if (!response.content.length) {
        footer.pages.innerHTML = "";
    } else {
        const to: number = response.from + response.content.length - 1;
        footer.pages.innerHTML = `<span>${response.from}${to !== response.from ? `-${to}` : ""} / ${response.count}</span>`;
    }

    footer.sum.innerHTML = response.sumValue || "";
    footer.sum.classList.toggle("mb-2", !!response.sumValue);
}

function createSpinner(): HTMLSpanElement {
    const spinner: HTMLSpanElement = document.createElement("span");
    spinner.className = "spinner-border spinner-border-sm me-1";
    spinner.role = "status";
    spinner.ariaHidden = "true";
    return spinner;
}

function onClickActionButton(
    options: IHTMLTableOptions,
    tr: HTMLTableRowElement,
    formsParent: HTMLElement,
    item: any,
    button: HTMLButtonElement,
    action: ITableAction,
    loadingButton: HTMLButtonElement,
) {
    button.addEventListener("click", async () => {
        if (action.prompt) {
            const text: string = action.prompt === true ? `Are you sure you want to ${action.text}?` : action.prompt;
            if (!confirm(text)) {
                return;
            }
        }
        if (!action.form) {
            loadingButton.classList.add("loading");
            const actionResponse: IRowResponseData<any> = await sendRequest(options.actionURL, {
                uid: item.uid,
                action: action.name,
            });
            loadingButton.classList.remove("loading");
            if (actionResponse.status === "error") {
                showToastError("danger", actionResponse.msg);
                return;
            }
            if (actionResponse.status === "redirect" && actionResponse.redirect) {
                location.href = actionResponse.redirect;
                return;
            }
            if (actionResponse.status === "showP2PForm" && actionResponse.formData) {
                showP2PForm(options, formsParent, item, actionResponse.formData);
                return;
            }
            if (actionResponse.status === "showC2CForm" && actionResponse.formData) {
                showC2CForm(options, formsParent, item, actionResponse.formData);
                return;
            }
            if (actionResponse.status === "showCardsList" && actionResponse.formData) {
                showCardsList(options, formsParent, item, actionResponse.formData);
                return;
            }
            if (actionResponse.status === "showDocsList" && actionResponse.formData) {
                showDocsList(options, formsParent, item, actionResponse.formData);
                return;
            }
            if (actionResponse.status === "showCardsTSum" && actionResponse.formData) {
                showCardsTSum(options, formsParent, item, actionResponse.formData);
                return;
            }
            if (actionResponse.status === "showSimpleForm" && actionResponse.formData) {
                showSimpleForm(options, formsParent, item, actionResponse.formData);
                return;
            }
            fillLine(options, tr, actionResponse.data);
        } else if (options[action.form]?.action) {
            const formSettings: IHTMLFormSettings = options[action.form];
            const {form, fillForm}: ISimpleForm = makeSimpleForm(formSettings);
            fillForm(item);
            const {parent, body}: IFixedForm = makeFixedForm(formSettings.title);
            form.onResponse = (text: string) => {
                const errorID: string = searchErrorID(form);
                try {
                    const responseData: IRowResponseData<any> = JSON.parse(text);
                    if (responseData.status === "error") {
                        errorID && showFormError(errorID, "danger", responseData.msg);
                        return;
                    }
                    if (responseData.status === "redirect" && responseData.redirect) {
                        location.href = responseData.redirect;
                        return;
                    }
                    fillLine(options, tr, responseData.data);
                    parent.classList.remove("show");
                    parent.remove();
                } catch (err) {
                    errorID && showFormError(errorID, "danger", err.toString());
                }
            };
            body.append(form);
            formsParent.append(parent);

            parent.addEventListener("click", (event) => {
                if (event.target !== parent) {
                    return;
                }
                event.stopPropagation();
                parent.remove();
            });
            parent.classList.add("show");
        }
    });

    if (!loadingButton.classList.contains("loadingButton")) {
        loadingButton.classList.add("loadingButton");
        const spinner: HTMLDivElement = document.createElement("div");
        spinner.className = "spinner-border spinner-border-sm text-info";
        spinner.role = "status";
        spinner.innerHTML = "<span class='visually-hidden'>Loading...</span>";
        loadingButton.append(spinner);
    }
}

function fillCell(
    options: IHTMLTableOptions,
    td: HTMLTableCellElement,
    name: string,
    item: any,
) {
    const columnSettings: IColumnSettings = options.columns[name];
    const value: string = item[name] ?? "";
    const valueSettings: IColumnValueSettings = columnSettings.values?.[value];
    if (!valueSettings) {
        td.innerHTML = value;
        return;
    }

    const span: HTMLSpanElement = document.createElement("span");
    span.className = "colorizedValue";
    span.innerHTML = valueSettings.text ?? value;
    span.style.backgroundColor = valueSettings.backgroundColor;
    span.style.color = valueSettings.textColor;

    td.innerHTML = "";
    td.append(span);
}

function fillLine(
    options: IHTMLTableOptions,
    tr: HTMLTableRowElement,
    item: any,
) {
    tr.innerHTML = "";
    tr.setAttribute("data-uid", item.uid);
    for (const name in options.columns) {
        const td: HTMLTableCellElement = document.createElement("td");
        if (item.color) {
            td.style.color = item.color;
        }
        if (name !== "actions") {
            fillCell(options, td, name, item);
        } else {
            const actions: ReadonlyArray<ITableAction> = item.actions ?? [];
            const formsParent: HTMLDivElement = document.createElement("div");
            td.append(formsParent);
            if (actions.length < 3) {
                td.className = actions.length ? "actionsBlock" : "";
                actions.forEach(action => {
                    const button: HTMLButtonElement = makeActionButton(action, true);
                    onClickActionButton(options, tr, formsParent, item, button, action, button);
                    td.append(button);
                });
            } else {
                const div: HTMLDivElement = document.createElement("div");
                div.className = "dropdown actionsDropdown";
                const button: HTMLButtonElement = makeButton("btn btn-light", "cil-apps-settings");
                button.classList.add("dropdown-toggle");
                button.setAttribute("data-coreui-toggle", "dropdown");
                button.ariaExpanded = "false";

                const menu: HTMLUListElement = document.createElement("ul");
                menu.className = "dropdown-menu";

                actions.forEach(action => {
                    const actionButton: HTMLButtonElement = makeActionButton(action, false);
                    actionButton.className = "dropdown-item";
                    onClickActionButton(options, tr, formsParent, item, actionButton, action, button);

                    const li: HTMLLIElement = document.createElement("li");
                    li.append(actionButton);
                    menu.append(li);
                });

                div.append(button, menu);
                td.append(div);
            }
        }
        tr.append(td);
    }
}

function fillTable(
    options: IHTMLTableOptions,
    _defaultSettings: IHTMLTableSettings,
    table: ITableBody,
    _request: ITableRequestData,
    response: ITableResponseData<any>,
    _update: TUpdateFunction,
) {
    if (!response.content.length) {
        table.tbody.innerHTML = `<tr>
                    <td class="text-center" colspan="${Object.keys(options.columns).length}">Nothing found</td>
                </tr>`;
    } else {
        table.tbody.innerHTML = "";
        response.content.forEach(item => {
            const tr: HTMLTableRowElement = document.createElement("tr");
            fillLine(options, tr, item);
            table.tbody.append(tr);
        });
    }
}

export function buildTable(
    options: IHTMLTableOptions,
    defaultSettings: IHTMLTableSettings,
    onChange?: TOnTableChangeCb,
): HTMLElement {
    defaultSettings = Object.assign({
        search: "",
        filter: {},
        sort: "",
        page: 1,
        countPerPage: 300,
    }, defaultSettings ?? {});

    let beforeUpdate: TUpdateFunction = (_request: ITableRequestData) => {};
    let afterUpdate: TOnTableChangeCb = (_request: ITableRequestData, _response: ITableResponseData<any>) => {};
    const update: TUpdateFunction = async (request: ITableRequestData) => {
        let response: ITableResponseData<any>;
        try {
            beforeUpdate(request);
            response = await sendRequest(options.loadDataURL, request);
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            afterUpdate(request, response);
        }
    };

    const head: ITableHead = makeTableHead(options, defaultSettings, update);
    const table: ITableBody = makeMainTable(options, defaultSettings, update);
    const footer: ITableFooter = makeTableFooter(options, defaultSettings, update);
    beforeUpdate = (_request: ITableRequestData) => {
        head.updateButton.classList.add("loading");
    };
    afterUpdate = (request: ITableRequestData, response: ITableResponseData<any>) => {
        head.updateButton.classList.remove("loading");
        setPagination(defaultSettings, footer, response, update);
        setPages(footer, response);
        fillTable(options, defaultSettings, table, request, response, update);
        onChange && onChange(request, response);
    };

    window.globals.lastUpdateFunction = update;
    window.globals.lastDefaultSettings = defaultSettings;

    const parent: HTMLDivElement = document.createElement("div");
    parent.append(head.head, table.parent, footer.parent);
    update(defaultSettings);
    return parent;
}

export function showFormError(id: string, type: string, message: string) {
    document.getElementById(id).innerHTML = `<div class="alert alert-${type} alert-dismissible" role="alert">
    <div>${message}</div>
    <button type="button" class="btn-close" data-coreui-dismiss="alert" aria-label="Close" onclick="document.getElementById('${id}').innerHTML ='';"></button>
</div>`;
}

export function showToastError(type: string, message: string) {
    const parent: HTMLElement = document.getElementById("globalAlertsArea");
    if (!parent) {
        return;
    }
    const toast: HTMLElement = document.createElement("div");
    toast.className = "toast fade mb-0";
    toast.role = "alert";
    toast.ariaLive = "assertive";
    toast.ariaAtomic = "true";
    toast.innerHTML = `<div class="toast-body">
        <div class="alert alert-${type} alert-dismissible mb-0" role="alert">
            <div>${message}</div>
        </div>
    </div>`;
    parent.append(toast);
    toast.classList.add("show", "showing");
    setTimeout(() => {
        toast.classList.remove("showing");
    }, 100);
    setTimeout(() => {
        toast.classList.add("hide", "showing");
    }, 39500);
    setTimeout(() => {
        toast.classList.remove("show");
        toast.remove();
    }, 40000);
}

export function submitForm(form: HTMLFormElement) {
    const data: FormData = new FormData(form);
    const json: any = Object.fromEntries(data.entries());

    const xhr: XMLHttpRequest = new XMLHttpRequest();
    xhr.onload = () => {
        if (xhr.readyState !== 4) {
            return;
        }
        if (!form.onResponse) {
            return;
        }
        form.onResponse(xhr.responseText);
    };
    xhr.open("POST", form.action, true);
    xhr.setRequestHeader("content-type", "application/json; charset=utf-8");
    xhr.send(JSON.stringify(json));
}

export function handleTableFormSubmit(form: HTMLFormElement) {
    if (form.getAttribute("submitListener")) {
        return;
    }
    form.addEventListener("submit", function(event: SubmitEvent) {
        event.preventDefault();
        event.stopPropagation();

        submitForm(form);
    });
    form.setAttribute("submitListener", "true");
}

export function createRawHiddenElement(element: IHTMLFormElement): HTMLInputElement {
    const input: HTMLInputElement = document.createElement("input");
    input.type = "hidden";
    input.name = element.name;
    input.value = element.value;
    return input;
}

export function showC2CForm(
    options: IHTMLTableOptions,
    formsParent: HTMLElement,
    item: IAccountsTableContent,
    data: IP2PFormAccountData,
) {
    const form: HTMLFormElement = document.createElement("form");
    form.action = "/account/c2c";
    handleTableFormSubmit(form);

    form.append(createSimpleFormElement({
        name: "_label",
        type: "label",
        label: `Account: ${item.username}`,
    }));

    form.append(createSimpleFormElement({
        name: "uid",
        type: "hidden",
        value: item.uid,
    }));

    const actionInput: HTMLInputElement = createRawHiddenElement({
        name: "button",
        value: "send_money",
        type: "hidden",
    });
    form.append(actionInput);

    form.append(createSimpleFormElement({
        label: "Select account:",
        name: "cardId",
        type: "dropdown",
        items: data.cards.map(card => ({
            value: card.id.toString(),
            text: `${card.title} ${card.balance} ${card.currency}${card.limit ? " (limit)" : ""}`,
        })),
        selectedItemValue: data.cards[0].id,
    }));

    form.append(createSimpleFormElement({
        name: "_error",
        type: "error",
    }));

    const buttons: HTMLDivElement = document.createElement("div");
    buttons.className = "d-flex flex-row mb-2";

    const sendButton: HTMLButtonElement = document.createElement("button");
    sendButton.type = "button";
    sendButton.className = "btn btn-primary loadingButton";
    sendButton.append(createSpinner(), "Transfer money");
    sendButton.addEventListener("click", () => {
        actionInput.value = "send_money";
        sendButton.classList.add("loading");
        submitForm(form);
    });

    buttons.append(sendButton);
    form.append(buttons);

    const {parent, body}: IFixedForm = makeFixedForm(data.title);
    form.onResponse = (text: string) => {
        sendButton.classList.remove("loading");
        const errorID: string = searchErrorID(form);
        try {
            const responseData: IP2PResponse = JSON.parse(text);
            if (responseData.status === "error") {
                errorID && showFormError(errorID, "danger", responseData.msg);
                return;
            }
            if (responseData.additionalInfo === "send_money") {
                showFormError(errorID, "success", responseData.msg || "Money successfully sent");
                return;
            }
            showFormError(errorID, "danger", "Please check transaction manually");
        } catch (err) {
            errorID && showFormError(errorID, "danger", err.toString());
        }
    };

    body.append(form);
    formsParent.append(parent);

    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        event.stopPropagation();
        parent.remove();
    });
    parent.classList.add("show");
}

export function showP2PForm(
    options: IHTMLTableOptions,
    formsParent: HTMLElement,
    item: IAccountsTableContent,
    data: IP2PFormAccountData,
) {
    const form: HTMLFormElement = document.createElement("form");
    form.action = data.action ?? "/account/p2p";
    handleTableFormSubmit(form);

    form.append(createSimpleFormElement({
        name: "_label",
        type: "label",
        label: `Account: ${item.username}`,
    }));

    form.append(createSimpleFormElement({
        name: "uid",
        type: "hidden",
        value: item.uid,
    }));

    const actionInput: HTMLInputElement = createRawHiddenElement({
        name: "button",
        value: "send_money",
        type: "hidden",
    });
    form.append(actionInput);

    const currencyInput: HTMLInputElement = createRawHiddenElement({
        name: "currency",
        value: data.cards[0].currency,
        type: "hidden",
    });
    form.append(currencyInput);

    const amount: HTMLElement = createSimpleFormElement({
        label: "Amount",
        name: "amount",
        type: "text",
        afterInput: data.cards[0].currency,
    });

    form.append(createSimpleFormElement({
        label: "Select account:",
        name: "cardId",
        type: "dropdown",
        items: data.cards.map(card => ({
            value: card.id.toString(),
            text: `${card.title} ${card.balance} ${card.currency}${card.limit ? " (limit)" : ""}`,
        })),
        selectedItemValue: data.cards[0].id,
        onChange: (result: IDropdownOnChangeResult) => {
            const card: IP2PCardData = data.cards.find(card => card.id.toString() === result.value);
            currencyInput.value = card.currency;
            const afterInput: HTMLElement = amount?.querySelector(".afterInput");
            if (afterInput) {
                afterInput.innerHTML = card.currency;
            }
        },
    }));

    if (data.transactionTypes?.length) {
        form.append(createSimpleFormElement({
            label: "Transaction type:",
            name: "transactionType",
            type: "dropdown",
            items: data.transactionTypes,
            selectedItemValue: data.transactionTypes[0].value,
            onChange: (result: IDropdownOnChangeResult) => {
                const type: ICurrencyDropdownItem = data.transactionTypes.find(t => t.value === result.value);
                if (!type?.currency) {
                    return;
                }
                currencyInput.value = type.currency;
                const afterInput: HTMLElement = amount?.querySelector(".afterInput");
                if (afterInput) {
                    afterInput.innerHTML = type.currency;
                }
            },
        }));
        const type: ICurrencyDropdownItem = data.transactionTypes[0];
        if (type?.currency) {
            currencyInput.value = type.currency;
            const afterInput: HTMLElement = amount?.querySelector(".afterInput");
            if (afterInput) {
                afterInput.innerHTML = type.currency;
            }
        }
    }

    const bankCard: HTMLElement = createSimpleFormElement({
        label: "Recipient's card",
        name: "bankCard",
        type: "text",
    });
    if (data.elements?.length) {
        form.append(amount);
        form.append(...data.elements.map(e => createSimpleFormElement(e)));
    } else {
        form.append(amount, bankCard);
    }

    form.append(createSimpleFormElement({
        name: "_error",
        type: "error",
    }));

    const commissionLine: HTMLDivElement = document.createElement("div");
    commissionLine.className = "fs08 mb-3";
    form.append(commissionLine);

    const buttons: HTMLDivElement = document.createElement("div");
    buttons.className = "d-flex flex-row mb-2";

    const checkButton: HTMLButtonElement = document.createElement("button");
    checkButton.type = "button";
    checkButton.className = "btn btn-light me-2 loadingButton";
    checkButton.append(createSpinner(), "Check commission");
    checkButton.addEventListener("click", () => {
        actionInput.value = "check_commission";
        checkButton.classList.add("loading");
        submitForm(form);
    });

    const bankCardInput: HTMLInputElement = bankCard?.querySelector("input");
    const amountInput: HTMLInputElement = amount.querySelector("input");
    let commissionTimeout: NodeJS.Timeout;

    function onDataChanges() {
        if (options.disableCheckCommission) {
            return;
        }
        if (commissionTimeout) {
            clearTimeout(commissionTimeout);
        }
        const card: string = bankCardInput?.value?.replace(/[^0-9]/g, "") ?? "";
        if (card.length < 16) {
            return;
        }
        const amountValue: string = amountInput?.value ?? "";
        const amountFixedValue: string = amountValue.replace(",", ".").replace(/[^0-9.]/, "");
        if (amountFixedValue && amountFixedValue !== amountValue) {
            amountInput.value = amountFixedValue;
        }
        commissionTimeout = setTimeout(() => {
            checkButton.click();
        }, 1000);
    }

    bankCardInput?.addEventListener("keyup", onDataChanges);
    amountInput?.addEventListener("keyup", onDataChanges);

    const sendButton: HTMLButtonElement = document.createElement("button");
    sendButton.type = "button";
    sendButton.className = "btn btn-primary loadingButton";
    sendButton.append(createSpinner(), "Send money");
    sendButton.addEventListener("click", () => {
        actionInput.value = "send_money";
        sendButton.classList.add("loading");
        submitForm(form);
    });

    if (!options.disableCheckCommission) {
        buttons.append(checkButton);
    }
    buttons.append(sendButton);
    form.append(buttons);

    const smsForm: HTMLFormElement = document.createElement("form");
    smsForm.action = data.confirmAction ?? "/account/confirmP2P";
    smsForm.className = "d-none";
    handleTableFormSubmit(smsForm);

    smsForm.append(createRawHiddenElement({
        name: "uid",
        value: item.uid,
        type: "hidden",
    }));

    const smsDataInput: HTMLInputElement = createRawHiddenElement({
        name: "data",
        value: "",
        type: "hidden",
    });
    smsForm.append(smsDataInput);

    smsForm.append(createSimpleFormElement({
        label: "SMS code:",
        name: "code",
        type: "text",
    }));

    smsForm.append(createSimpleFormElement({
        name: "_error",
        type: "error",
    }));

    const smsFormButton: HTMLElement = createSimpleFormElement({
        name: "_button",
        type: "button",
        className: "btn btn-primary loadingButton",
        value: "Send SMS code",
    });
    smsFormButton.children[0].addEventListener("click", () => {
        smsFormButton.children[0].classList.add("loading");
    });
    smsForm.append(smsFormButton);

    const {parent, body}: IFixedForm = makeFixedForm(data.title);
    form.onResponse = (text: string) => {
        checkButton.classList.remove("loading");
        sendButton.classList.remove("loading");
        const errorID: string = searchErrorID(form);
        try {
            const responseData: IP2PResponse = JSON.parse(text);
            if (responseData.status === "error") {
                errorID && showFormError(errorID, "danger", responseData.msg);
                return;
            }
            if (responseData.additionalInfo === "commission_info") {
                const data: IP2PCommissionInfo = responseData as IP2PCommissionInfo;
                commissionLine.innerHTML = `Commission: ${data.commission.toFixed(2)} ${data.currency || ""}`;
                if (data.conversionAmount) {
                    commissionLine.innerHTML += `<br>Conversion: ${data.conversionAmount.toFixed(2)} ${data.conversionCurrency}`;
                }
                return;
            }
            if (responseData.additionalInfo === "need_sms") {
                if (responseData.commission) {
                    commissionLine.innerHTML = `Commission: ${responseData.commission.toFixed(2)} ${responseData.currency || ""}`;
                }
                smsDataInput.value = responseData.data;
                smsForm.className = "";
                return;
            }
            if (responseData.additionalInfo === "need_3ds") {
                const {url}: {url: string} = JSON.parse(responseData.data);
                const a: HTMLAnchorElement = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.innerHTML = "Go to 3DSecure";
                showFormError(errorID, "info", a.outerHTML);
                return;
            }
            if (responseData.additionalInfo === "send_money") {
                if (responseData.commission) {
                    commissionLine.innerHTML = `Commission: ${responseData.commission.toFixed(2)} ${responseData.currency || ""}`;
                }
                showFormError(errorID, "success", responseData.msg || "Money successfully sent");
                return;
            }
            showFormError(errorID, "danger", "Please check transaction manually");
        } catch (err) {
            errorID && showFormError(errorID, "danger", err.toString());
        }
    };
    smsForm.onResponse = (text: string) => {
        smsFormButton.children[0].classList.remove("loading");
        const errorID: string = searchErrorID(smsForm);
        try {
            const responseData: IP2PResponse = JSON.parse(text);
            if (responseData.status === "error") {
                errorID && showFormError(errorID, "danger", responseData.msg);
                return;
            }
            if (responseData.additionalInfo === "send_money") {
                showFormError(errorID, "success", responseData.msg || "Money successfully sent");
                return;
            }
            showFormError(errorID, "danger", "Please check transaction manually");
        } catch (err) {
            errorID && showFormError(errorID, "danger", err.toString());
        }
    };

    body.append(form, smsForm);
    formsParent.append(parent);

    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        event.stopPropagation();
        parent.remove();
    });
    parent.classList.add("show");
}

export function fillCardInList(
    options: IHTMLTableOptions,
    item: IAccountsTableContent,
    li: HTMLLIElement,
    card: IP2PCardData,
) {
    li.innerHTML = "";
    const text: HTMLSpanElement = document.createElement("span");
    text.innerHTML = `${card.title} ${card.expDate ?? ""} ${card.cvc ?? ""} (${card.name ?? ""}) ${card.balance} ${card.currency} ${card.limitsLine ?? ""}`.trim();
    li.append(text);
    const showButton: boolean =
        !card.limitsLine?.includes("INTERNET: 500000")
        && !card.limitsLine?.includes("P2P: 350000/50")
        && options.bankName !== "RaiffeisenRU"
        && options.bankName !== "DCNext"
        && options.bankName !== "Agricole"
        && options.bankName !== "Sberbank"
        && options.bankName !== "RaiffeisenUA"
        && options.bankName !== "MyRaif"
        && options.bankName !== "PNBOne"
    ;
    if (card.active === false && !card.challenge) {
        if (options.bankName === "MyRaif") {
            const text: HTMLSpanElement = document.createElement("span");
            text.className = "cardLimits";
            text.innerHTML = " BLOCKED";
            li.append(text);
        } else {
            const sendSMSButton: HTMLButtonElement = document.createElement("button");
            sendSMSButton.type = "button";
            sendSMSButton.className = "btn btn-light me-2 loadingButton";
            sendSMSButton.append(createSpinner(), "Send SMS to unlock");
            sendSMSButton.addEventListener("click", async () => {
                sendSMSButton.classList.add("loading");
                try {
                    const actionResponse: IRowResponseData<IP2PCardData> = await sendRequest(options.activateCardURL, {
                        uid: item.uid,
                        cardId: card.id,
                    });
                    if (actionResponse.status === "error") {
                        showToastError("danger", actionResponse.msg);
                        return;
                    }
                    fillCardInList(options, item, li, actionResponse.data);
                } catch (err) {
                    showToastError("danger", err.toString());
                } finally {
                    sendSMSButton.classList.remove("loading");
                }
            });
            li.append(sendSMSButton);
        }
    } else if (card.active === false && card.challenge) {
        const input: HTMLInputElement = document.createElement("input");
        input.className = "form-control";
        input.type = "text";
        input.name = "otp";
        input.style.width = "100px";
        const unlockButton: HTMLButtonElement = document.createElement("button");
        unlockButton.type = "button";
        unlockButton.className = "btn btn-light me-2 loadingButton";
        unlockButton.append(createSpinner(), "Unlock");
        unlockButton.addEventListener("click", async () => {
            unlockButton.classList.add("loading");
            try {
                const actionResponse: IRowResponseData<IP2PCardData> = await sendRequest(options.activateCardURL, {
                    uid: item.uid,
                    cardId: card.id,
                    challenge: card.challenge,
                    otp: input.value,
                });
                if (actionResponse.status === "error") {
                    showToastError("danger", actionResponse.msg);
                    return;
                }
                fillCardInList(options, item, li, actionResponse.data);
            } catch (err) {
                showToastError("danger", err.toString());
            } finally {
                unlockButton.classList.remove("loading");
            }
        });
        li.append(input, unlockButton);
    } else if (showButton && card.active === true && !card.challenge) {
        const sendSMSButton: HTMLButtonElement = document.createElement("button");
        sendSMSButton.type = "button";
        sendSMSButton.className = "btn btn-light me-2 loadingButton";
        sendSMSButton.append(createSpinner(), "Send SMS to change limits");
        sendSMSButton.addEventListener("click", async () => {
            sendSMSButton.classList.add("loading");
            try {
                const actionResponse: IRowResponseData<IP2PCardData> = await sendRequest(options.changeLimitsURL, {
                    uid: item.uid,
                    cardId: card.id,
                });
                if (actionResponse.status === "error") {
                    showToastError("danger", actionResponse.msg);
                    return;
                }
                fillCardInList(options, item, li, actionResponse.data);
            } catch (err) {
                showToastError("danger", err.toString());
            } finally {
                sendSMSButton.classList.remove("loading");
            }
        });
        li.append(sendSMSButton);
    } else if (showButton && card.active === true && card.challenge) {
        const input: HTMLInputElement = document.createElement("input");
        input.className = "form-control";
        input.type = "text";
        input.name = "otp";
        input.style.width = "100px";
        const changeLimitsButton: HTMLButtonElement = document.createElement("button");
        changeLimitsButton.type = "button";
        changeLimitsButton.className = "btn btn-light me-2 loadingButton";
        changeLimitsButton.append(createSpinner(), "Change limits");
        changeLimitsButton.addEventListener("click", async () => {
            changeLimitsButton.classList.add("loading");
            try {
                const actionResponse: IRowResponseData<IP2PCardData> = await sendRequest(options.changeLimitsURL, {
                    uid: item.uid,
                    cardId: card.id,
                    challenge: card.challenge,
                    otp: input.value,
                });
                if (actionResponse.status === "error") {
                    showToastError("danger", actionResponse.msg);
                    return;
                }
                fillCardInList(options, item, li, actionResponse.data);
            } catch (err) {
                showToastError("danger", err.toString());
            } finally {
                changeLimitsButton.classList.remove("loading");
            }
        });
        if (card.challenge !== "no sms") {
            li.append(input);
        }
        li.append(changeLimitsButton);
    }

    if (options.setPinURL) {
        const pinInput: HTMLInputElement = document.createElement("input");
        pinInput.className = "form-control";
        pinInput.type = "text";
        pinInput.name = "pin";
        pinInput.style.width = "100px";
        pinInput.style.marginRight = "10px";
        pinInput.value = "1111";
        const setPinButton: HTMLButtonElement = document.createElement("button");
        setPinButton.type = "button";
        setPinButton.className = "btn btn-light me-2 loadingButton";
        setPinButton.append(createSpinner(), "Set PIN");
        setPinButton.addEventListener("click", async () => {
            setPinButton.classList.add("loading");
            try {
                const actionResponse: IRowResponseData<IP2PCardData> = await sendRequest(options.setPinURL, {
                    uid: item.uid,
                    cardId: card.id,
                    challenge: card.challenge,
                    pin: pinInput.value,
                });
                if (actionResponse.status === "error") {
                    showToastError("danger", actionResponse.msg);
                    return;
                }
                showToastError("success", actionResponse.msg ?? "PIN successfully set");
            } catch (err) {
                showToastError("danger", err.toString());
            } finally {
                setPinButton.classList.remove("loading");
            }
        });
        const line: HTMLSpanElement = document.createElement("span");
        line.style.display = "flex";
        line.append(pinInput, setPinButton);
        li.append(line);
    }
}

export function showSimpleForm(
    options: IHTMLTableOptions,
    formsParent: HTMLElement,
    item: IAccountsTableContent,
    formSettings: IHTMLFormSettings,
) {
    const {form, fillForm}: ISimpleForm = makeSimpleForm(formSettings);
    fillForm(item);
    const {parent, body}: IFixedForm = makeFixedForm(formSettings.title);
    form.onResponse = (text: string) => {
        const errorID: string = searchErrorID(form);
        try {
            const responseData: IRowResponseData<any> = JSON.parse(text);
            if (responseData.status === "error") {
                errorID && showFormError(errorID, "danger", responseData.msg);
                return;
            }
            if (responseData.status === "redirect" && responseData.redirect) {
                location.href = responseData.redirect;
                return;
            }
            if (responseData.status === "ok") {
                errorID && showFormError(errorID, "success", "Success");
                setTimeout(() => {
                    parent.classList.remove("show");
                    parent.remove();
                }, 2000);
                return;
            }
            parent.classList.remove("show");
            parent.remove();
        } catch (err) {
            errorID && showFormError(errorID, "danger", err.toString());
        }
    };
    body.append(form);
    formsParent.append(parent);

    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        event.stopPropagation();
        parent.remove();
    });
    parent.classList.add("show");
}

export function showCardsTSum(
    options: IHTMLTableOptions,
    formsParent: HTMLElement,
    item: IAccountsTableContent,
    data: ICardsTSumAccountData,
) {
    const {parent, body}: IFixedForm = makeFixedForm("Cards transactions count");
    const list: HTMLDivElement = document.createElement("div");

    list.append(createSimpleFormElement({
        name: "_label",
        type: "label",
        label: `Account: ${item.username}`,
    }));

    const innerOptions: IHTMLTableOptions = {
        loadDataURL: "", actionURL: "", isSearchBox: false,
        columns: {
            title: {title: "Card"},
            last24h: {title: "Today (from 01:00 msk)"},
            prev24h: {title: "Yesterday"},
            last7d: {title: "Week (from 01:00 msk of Mon)"},
            last30d: {title: "Month (from 01:00 msk of 1 day)"},
        },
    };

    const table: ITableBody = makeMainTable(innerOptions, null, null);

    fillTable(innerOptions, null, table, null, {
        content: data.cards,
        status: "ok",
        page: 1,
        count: data.cards.length,
        from: 1,
        lastPage: 1,
    }, null);

    list.append(table.parent);

    body.append(list);

    formsParent.append(parent);

    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        event.stopPropagation();
        parent.remove();
    });
    parent.classList.add("show");
}

export function showDocsList(
    _options: IHTMLTableOptions,
    formsParent: HTMLElement,
    item: IAccountsTableContent,
    data: IDocsData,
) {
    const {parent, body}: IFixedForm = makeFixedForm(data.title);
    const list: HTMLDivElement = document.createElement("div");

    list.append(createSimpleFormElement({
        name: "_label",
        type: "label",
        label: `Account: ${item.username}`,
    }));

    const ul: HTMLUListElement = document.createElement("ul");
    ul.append(...data.docs.map(doc => {
        const li: HTMLLIElement = document.createElement("li");
        const a: HTMLAnchorElement = document.createElement("a");
        a.href = doc.link;
        a.target = "_blank";
        a.innerHTML = doc.title;
        li.append(a);
        return li;
    }));

    if (!data.docs?.length) {
        const li: HTMLLIElement = document.createElement("li");
        li.innerHTML = "No docs";
        ul.append(li);
    }

    list.append(ul);

    body.append(list);

    if (data.askNew) {
        const askNewDocButton: HTMLButtonElement = document.createElement("button");
        askNewDocButton.type = "button";
        askNewDocButton.className = "btn btn-light me-2 loadingButton";
        askNewDocButton.append(createSpinner(), "Ask new doc");
        askNewDocButton.addEventListener("click", async () => {
            askNewDocButton.classList.add("loading");
            try {
                const actionResponse: IAskNewDoc = await sendRequest(data.askNew, {
                    uid: item.uid,
                });
                if (actionResponse.status === "error") {
                    showToastError("danger", actionResponse.msg);
                    return;
                }
                showToastError("success", actionResponse.msg ?? "Doc successfully ordered");
            } catch (err) {
                showToastError("danger", err.toString());
            } finally {
                askNewDocButton.classList.remove("loading");
            }
        });
        body.append(askNewDocButton);
    }

    formsParent.append(parent);

    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        event.stopPropagation();
        parent.remove();
    });
    parent.classList.add("show");
}

export function showCardsList(
    options: IHTMLTableOptions,
    formsParent: HTMLElement,
    item: IAccountsTableContent,
    data: IP2PFormAccountData,
) {
    const {parent, body}: IFixedForm = makeFixedForm(data.title);
    const list: HTMLDivElement = document.createElement("div");

    list.append(createSimpleFormElement({
        name: "_label",
        type: "label",
        label: `Account: ${item.username}`,
    }));

    const ul: HTMLUListElement = document.createElement("ul");
    ul.append(...data.cards.map(card => {
        const li: HTMLLIElement = document.createElement("li");
        fillCardInList(options, item, li, card);
        return li;
    }));
    list.append(ul);

    body.append(list);

    if (options.orderCardURL) {
        const emailField: HTMLInputElement = document.createElement("input");
        emailField.className = "form-control";
        emailField.type = "text";
        emailField.name = "email";
        emailField.style.width = "200px";
        const domains: string[] = ["gmail.com", "outlook.com", "kablanmoh.site", "cheshpo.com", "cloneads.top"];
        emailField.value = (Date.now() * Math.random()).toString(36).replace(/\./g, "")
            + "@"
            + domains[Math.random() * domains.length | 0];
        emailField.placeholder = "Email";

        const smsField: HTMLInputElement = document.createElement("input");
        smsField.className = "form-control";
        smsField.type = "hidden";
        smsField.name = "sms";
        smsField.style.width = "200px";
        smsField.value = "";
        smsField.placeholder = "SMS-code";

        const smsDataInput: HTMLInputElement = createRawHiddenElement({
            name: "data",
            value: "",
            type: "hidden",
        });

        const orderCardButton: HTMLButtonElement = document.createElement("button");
        orderCardButton.type = "button";
        orderCardButton.className = "btn btn-light me-2 loadingButton";
        orderCardButton.append(createSpinner(), "Order new card");
        orderCardButton.addEventListener("click", async () => {
            orderCardButton.classList.add("loading");
            try {
                const actionResponse: IRowResponseData<string> = await sendRequest(options.orderCardURL, {
                    uid: item.uid,
                    email: emailField.value,
                    sms: smsField.value,
                    data: smsDataInput.value,
                });
                if (actionResponse.status === "error") {
                    showToastError("danger", actionResponse.msg);
                    return;
                }
                if (actionResponse.status === "need_sms") {
                    smsDataInput.value = actionResponse.data;
                    smsField.type = "text";
                    emailField.type = "hidden";
                    orderCardButton.innerHTML = "";
                    orderCardButton.append(createSpinner(), "Send SMS code");
                    return;
                }
                if (actionResponse.status === "need_email") {
                    smsDataInput.value = actionResponse.data;
                    smsField.type = "hidden";
                    emailField.type = "text";
                    emailField.value = actionResponse.formData?.email || "";
                    orderCardButton.innerHTML = "";
                    orderCardButton.append(createSpinner(), "Send Email");
                    return;
                }
                showToastError("success", "Card successfully ordered");
            } catch (err) {
                showToastError("danger", err.toString());
            } finally {
                orderCardButton.classList.remove("loading");
            }
        });
        if (options.bankName === "Kredobank") {
            body.append(emailField, smsDataInput, smsField);
        } else if (options.bankName === "MyRaif") {
            emailField.type = "hidden";
            emailField.value = "";
            body.append(emailField, smsDataInput, smsField);
        }
        body.append(orderCardButton);
    }

    formsParent.append(parent);

    parent.addEventListener("click", (event) => {
        if (event.target !== parent) {
            return;
        }
        event.stopPropagation();
        parent.remove();
    });
    parent.classList.add("show");
}

export function updateUnknownOutgoings(startTimestamp: number, currency: string, key: string) {
    if (!window.globals.lastUpdateFunction || !window.globals.lastDefaultSettings) {
        return;
    }

    window.globals.lastDefaultSettings.filter.statisticSettings = {startTimestamp, currency, key};
    if (!startTimestamp && !currency && !key) {
        delete window.globals.lastDefaultSettings.filter.statisticSettings;
    }

    window.globals.lastUpdateFunction(window.globals.lastDefaultSettings);
}
